import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../lib/api'
import DesignSupportLayout from './designSupport/DesignSupportLayout'

const PAGE_SIZE = 50

export default function DesignSupportMajorDiseaseNPage() {
  const [insurerCode, setInsurerCode] = useState('')
  const [productCode, setProductCode] = useState('')
  const [nLabel, setNLabel] = useState('')
  const [q, setQ] = useState('')
  const [debounced, setDebounced] = useState('')
  const [page, setPage] = useState(1)
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState('')

  useEffect(() => {
    const t = setTimeout(() => setDebounced(String(q).trim()), 220)
    return () => clearTimeout(t)
  }, [q])

  useLayoutEffect(() => {
    setPage(1)
  }, [debounced, insurerCode, productCode, nLabel])

  const fetchPage = useCallback(
    (pageNum) => {
      setLoading(true)
      setErr('')
      api
        .get('/operator/design-support/major-disease-n/search', {
          params: {
            insurerCode: insurerCode.trim(),
            productCode: productCode.trim(),
            nLabel: nLabel.trim(),
            q: debounced,
            page: pageNum,
            size: PAGE_SIZE,
          },
        })
        .then((res) => {
          const data = res?.data?.data
          setRows(Array.isArray(data?.items) ? data.items : [])
          setTotal(Number(data?.total) || 0)
        })
        .catch(() => {
          setRows([])
          setTotal(0)
          setErr('목록을 불러오지 못했습니다.')
        })
        .finally(() => setLoading(false))
    },
    [debounced, insurerCode, productCode, nLabel],
  )

  useEffect(() => {
    fetchPage(page)
  }, [fetchPage, page])

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  return (
    <DesignSupportLayout>
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <h2 className="text-lg font-semibold text-slate-900">N대 질병 참고 (운영)</h2>
          <Link to="/data/design-support-reference" className="shrink-0 text-xs font-medium text-brand-sky hover:underline sm:text-sm">
            데이터 적재 →
          </Link>
        </div>
      <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-relaxed text-amber-950 space-y-2">
        <p>
          본 화면은 <strong>보험사 약관·특약을 대신하거나 가입 권유·보장을 확정하지 않습니다.</strong> 원수사·상품별로 정리한{' '}
          <strong>참고용 큐레이션</strong>이며, 실제 지급·보장 범위는 <strong>해당 계약의 약관·통지·보험사 안내가 우선</strong>합니다. 표시 데이터는 DB(
          <code className="text-xs">work_n_major_disease_row</code>)에 적재된 내용만 반영됩니다.
        </p>
      </div>

      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <label className="block text-xs font-medium text-slate-600">원수사 코드</label>
          <input
            className="mt-0.5 w-full rounded border border-slate-300 px-3 py-2 text-sm"
            value={insurerCode}
            onChange={(e) => setInsurerCode(e.target.value)}
            placeholder="예: SAMSUNG"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600">상품 코드</label>
          <input
            className="mt-0.5 w-full rounded border border-slate-300 px-3 py-2 text-sm"
            value={productCode}
            onChange={(e) => setProductCode(e.target.value)}
            placeholder="상품 식별자"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600">N 라벨</label>
          <input
            className="mt-0.5 w-full rounded border border-slate-300 px-3 py-2 text-sm"
            value={nLabel}
            onChange={(e) => setNLabel(e.target.value)}
            placeholder="예: 3대, 7대"
          />
        </div>
        <div className="sm:col-span-2 lg:col-span-4">
          <label className="block text-sm font-medium text-slate-700">통합 검색 (KCD·질병명·원수사코드)</label>
          <input
            className="mt-1 w-full max-w-xl rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none focus:ring-1 focus:ring-brand-sky"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="키워드 (비우면 필터만 적용)"
          />
        </div>
      </div>

      {err && <p className="mt-2 text-xs text-red-600">{err}</p>}
      <p className="mt-2 text-xs text-slate-500">
        {loading ? '불러오는 중…' : `전체 ${total.toLocaleString()}건 · 페이지 ${page} / ${totalPages}`}
      </p>

      <div className="mt-4 overflow-x-auto rounded border border-slate-200">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold text-slate-600">
            <tr>
              <th className="px-3 py-2 whitespace-nowrap">원수사</th>
              <th className="px-3 py-2 whitespace-nowrap">상품</th>
              <th className="px-3 py-2">N</th>
              <th className="px-3 py-2">KCD</th>
              <th className="px-3 py-2 min-w-[10rem]">질병명</th>
              <th className="px-3 py-2 whitespace-nowrap">적용 시작</th>
              <th className="px-3 py-2 whitespace-nowrap">적용 종료</th>
              <th className="px-3 py-2 min-w-[8rem]">공시 URL</th>
              <th className="px-3 py-2 min-w-[8rem]">메모</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-3 py-10 text-center text-slate-500">
                  {loading ? '불러오는 중…' : '데이터가 없습니다. 데이터관리에서 CSV를 적재한 뒤 다시 조회하세요.'}
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100">
                  <td className="px-3 py-2 text-xs">
                    <span className="font-mono">{r.insurerCode}</span>
                    <span className="block text-slate-500">{r.insurerName || '—'}</span>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{r.productCode}</td>
                  <td className="px-3 py-2 text-xs">{r.nLabel || '—'}</td>
                  <td className="px-3 py-2 font-mono text-xs">{r.kcdCategory || '—'}</td>
                  <td className="px-3 py-2">{r.diseaseNameKo || '—'}</td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap">{r.effectiveFrom || '—'}</td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap">{r.effectiveTo || '—'}</td>
                  <td className="px-3 py-2 text-xs break-all max-w-[12rem]">
                    {r.disclosureUrl ? (
                      <a className="text-brand-sky underline" href={r.disclosureUrl} target="_blank" rel="noreferrer noopener">
                        링크
                      </a>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600 max-w-[14rem] truncate" title={r.sourceNote || ''}>
                    {r.sourceNote || '—'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="mt-4 flex items-center gap-3 text-sm">
          <button
            type="button"
            className="rounded border border-slate-300 px-3 py-1.5 hover:bg-slate-50 disabled:opacity-40"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            이전
          </button>
          <span className="text-slate-600">
            {page} / {totalPages}
          </span>
          <button
            type="button"
            className="rounded border border-slate-300 px-3 py-1.5 hover:bg-slate-50 disabled:opacity-40"
            disabled={page >= totalPages || loading}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            다음
          </button>
        </div>
      )}
      </div>
    </DesignSupportLayout>
  )
}
